import { useState } from 'react';
import { Lock, Shield, Key, Smartphone, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface SecuritySettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SecuritySettingsModal({ isOpen, onClose }: SecuritySettingsModalProps) {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: '',
  });

  const handleChangePassword = () => {
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    if (passwordData.new !== passwordData.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwordData.new.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    toast.success('Contraseña actualizada exitosamente');
    setPasswordData({ current: '', new: '', confirm: '' });
  };

  const handleToggle2FA = () => {
    setTwoFactorEnabled(!twoFactorEnabled);
    if (!twoFactorEnabled) {
      toast.success('Autenticación de dos factores activada');
    } else {
      toast.success('Autenticación de dos factores desactivada');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <Shield className="h-5 w-5 text-pink-600 dark:text-pink-400" />
            <span>Configuración de Seguridad</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Security Status */}
          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-4 dark:border-green-800 dark:from-green-900/20 dark:to-emerald-900/20">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" />
              <div>
                <h4 className="mb-1 text-sm font-semibold text-green-900 dark:text-green-100">
                  Tu cuenta está protegida
                </h4>
                <p className="text-xs text-green-700 dark:text-green-300">
                  Última actualización de contraseña: 15 de Octubre, 2025
                </p>
              </div>
            </div>
          </Card>

          {/* Change Password Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-pink-600 dark:text-pink-400" />
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">Cambiar Contraseña</h3>
            </div>

            <div className="space-y-4 pl-7">
              <div className="space-y-2">
                <Label htmlFor="current-password" className="text-gray-700 dark:text-gray-300">
                  Contraseña Actual
                </Label>
                <Input
                  id="current-password"
                  type="password"
                  value={passwordData.current}
                  onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                  className="border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
                  placeholder="Ingresa tu contraseña actual"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-gray-700 dark:text-gray-300">
                  Nueva Contraseña
                </Label>
                <Input
                  id="new-password"
                  type="password"
                  value={passwordData.new}
                  onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                  className="border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
                  placeholder="Mínimo 8 caracteres"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-gray-700 dark:text-gray-300">
                  Confirmar Nueva Contraseña
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={passwordData.confirm}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                  className="border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800"
                  placeholder="Repite la nueva contraseña"
                />
              </div>

              <Button
                onClick={handleChangePassword}
                className="bg-gradient-to-r from-pink-600 to-rose-600 text-white hover:from-pink-700 hover:to-rose-700"
              >
                Actualizar Contraseña
              </Button>
            </div>
          </div>

          {/* Two-Factor Authentication */}
          <div className="space-y-4 border-t border-gray-200 pt-4 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-pink-600 dark:text-pink-400" />
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Autenticación de Dos Factores
              </h3>
            </div>

            <Card className="border-gray-200 p-4 dark:border-gray-800">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="mb-2 flex items-center gap-2">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      Autenticación 2FA
                    </h4>
                    {twoFactorEnabled ? (
                      <Badge className="border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                        Activo
                      </Badge>
                    ) : (
                      <Badge className="border-gray-200 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                        Inactivo
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Agrega una capa extra de seguridad requiriendo un código de verificación al
                    iniciar sesión.
                  </p>
                </div>
                <Switch
                  checked={twoFactorEnabled}
                  onCheckedChange={handleToggle2FA}
                  className="data-[state=checked]:bg-pink-600"
                />
              </div>
            </Card>

            {twoFactorEnabled && (
              <Card className="border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
                  <div>
                    <h4 className="mb-1 text-sm font-semibold text-blue-900 dark:text-blue-100">
                      Configuración Adicional Requerida
                    </h4>
                    <p className="mb-3 text-xs text-blue-700 dark:text-blue-300">
                      Para completar la configuración de 2FA, escanea el código QR con tu aplicación
                      de autenticación.
                    </p>
                    <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700">
                      Configurar Ahora
                    </Button>
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Session Management */}
          <div className="space-y-4 border-t border-gray-200 pt-4 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <Key className="h-5 w-5 text-pink-600 dark:text-pink-400" />
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Gestión de Sesiones
              </h3>
            </div>

            <Card className="border-gray-200 p-4 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="mb-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                    Sesiones Activas
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Tienes 1 sesión activa actualmente
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                  onClick={() => toast.success('Todas las demás sesiones han sido cerradas')}
                >
                  Cerrar Todas
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
